import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import LeagueService from '../services/LeagueService';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple } from '../utils/vfx';
import {
  FaHome,
  FaGraduationCap,
  FaQuestionCircle,
  FaChartLine,
  FaFlask,
  FaUsers,
  FaBook,
  FaClipboardCheck,
  FaCheckCircle,
  FaTimesCircle,
  FaArrowRight,
  FaTrophy
} from 'react-icons/fa';
import BottomNav from './BottomNav';
import ProfileMenu from './ProfileMenu';
import './ClassAssignments.css';

const ClassAssignments = () => {
  const navigate = useNavigate();
  const { state } = useUser();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState({ lessons: [], quizzes: [] });
  const [activeTab, setActiveTab] = useState('lessons'); // 'lessons', 'quizzes', or 'challenges'

  useEffect(() => {
    if (!state.user?.leagueId) {
      toast.error('You are not part of a class');
      navigate('/dashboard', { replace: true });
      return;
    }

    loadAssignments();
  }, [state.user, navigate]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      const data = await LeagueService.getMyAssignments();
      setAssignments({
        lessons: data.lessons || [],
        quizzes: data.quizzes || []
      });
    } catch (error) {
      toast.error(error.error || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleStartLesson = (lessonId, courseId, unitId, source = 'global') => {
    soundEffects.playClick();
    
    // Teacher-created lessons use a different route
    // Check if courseId is missing, undefined, null, or 'undefined' string
    if (source === 'teacher' || !courseId || courseId === 'undefined' || courseId === 'null') {
      navigate(`/teacher-lesson/${lessonId}`);
      return;
    }
    
    // Ensure courseId is valid before navigating
    if (courseId && courseId !== 'undefined' && courseId !== 'null') {
      navigate(`/course/${courseId}/lesson/${lessonId}`);
    } else {
      console.warn('Invalid courseId for lesson:', lessonId, 'courseId:', courseId);
      navigate(`/teacher-lesson/${lessonId}`);
    }
  };

  const handleStartQuiz = (quizId, lessonId, courseId, source = 'global') => {
    soundEffects.playClick();
    
    // Teacher-created quizzes use a different route
    // Check if courseId is missing, undefined, null, or 'undefined' string
    if (source === 'teacher' || !courseId || courseId === 'undefined' || courseId === 'null') {
      navigate(`/teacher-quiz/${quizId}`);
      return;
    }
    
    // Ensure courseId is valid before navigating
    if (courseId && courseId !== 'undefined' && courseId !== 'null' && lessonId) {
      navigate(`/course/${courseId}/lesson/${lessonId}/quiz/${quizId}`);
    } else {
      console.warn('Invalid courseId or lessonId for quiz:', quizId, 'courseId:', courseId, 'lessonId:', lessonId);
      navigate(`/teacher-quiz/${quizId}`);
    }
  };

  if (loading) {
    return (
      <div className="class-assignments-container content-with-nav">
        <div className="loading-screen">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p className="loading-text">Loading assignments...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="class-assignments-container content-with-nav">
      <div className="class-assignments-header">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
          <ProfileMenu />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1>Class Assignments</h1>
            <p className="subtitle">Complete the lessons and quizzes assigned by your teacher</p>
          </div>
          <button
            onClick={(e) => {
              createRipple(e);
              soundEffects.playClick();
              navigate('/challenges');
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#d97706';
              soundEffects.playHover();
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f59e0b';
            }}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}
          >
            <FaTrophy /> League Challenges
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="assignments-tabs">
        <button
          className={`tab-button ${activeTab === 'lessons' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('lessons');
            soundEffects.playClick();
          }}
        >
          <FaBook /> Lessons ({assignments.lessons.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'quizzes' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('quizzes');
            soundEffects.playClick();
          }}
        >
          <FaClipboardCheck /> Quizzes ({assignments.quizzes.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'challenges' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('challenges');
            soundEffects.playClick();
            navigate('/challenges');
          }}
        >
          <FaTrophy /> Challenges
        </button>
      </div>

      {/* Content */}
      <div className="assignments-content">
        {activeTab === 'lessons' ? (
          <div className="assignments-list">
            {assignments.lessons.length > 0 ? (
              assignments.lessons.map((lesson) => (
                <motion.div
                  key={lesson.lessonId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`assignment-card lesson-card ${lesson.completed ? 'completed' : ''}`}
                >
                  <div className="assignment-header">
                    <div className="assignment-info">
                      <h3>{lesson.title}</h3>
                      <p className="assignment-meta">
                        {lesson.courseTitle} • {lesson.unitTitle}
                      </p>
                      <p className="assignment-date">
                        Assigned: {new Date(lesson.assignedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="assignment-status">
                      {lesson.completed ? (
                        <FaCheckCircle className="status-icon completed" />
                      ) : (
                        <FaTimesCircle className="status-icon pending" />
                      )}
                    </div>
                  </div>
                  <div className="assignment-actions">
                    <button
                      className="start-button"
                      onClick={(e) => {
                        createRipple(e);
                        handleStartLesson(lesson.lessonId, lesson.courseId, lesson.unitId, lesson.source);
                      }}
                    >
                      {lesson.completed ? 'Review Lesson' : 'Start Lesson'}
                      <FaArrowRight />
                    </button>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="empty-state">
                <FaBook size={48} />
                <p>No lessons assigned yet. Check back later!</p>
              </div>
            )}
          </div>
        ) : (
          <div className="assignments-list">
            {assignments.quizzes.length > 0 ? (
              assignments.quizzes.map((quiz) => (
                <motion.div
                  key={quiz.quizId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`assignment-card quiz-card ${quiz.attempted ? 'attempted' : ''}`}
                >
                  <div className="assignment-header">
                    <div className="assignment-info">
                      <h3>{quiz.title}</h3>
                      <p className="assignment-meta">
                        {quiz.courseTitle} • {quiz.unitTitle}
                      </p>
                      <p className="assignment-date">
                        Assigned: {new Date(quiz.assignedAt).toLocaleDateString()}
                      </p>
                      {quiz.attempted && quiz.bestScore !== null && (
                        <p className="quiz-score">
                          Best Score: {quiz.bestScore}%
                        </p>
                      )}
                    </div>
                    <div className="assignment-status">
                      {quiz.attempted ? (
                        <FaCheckCircle className="status-icon attempted" />
                      ) : (
                        <FaTimesCircle className="status-icon pending" />
                      )}
                    </div>
                  </div>
                  <div className="assignment-actions">
                    <button
                      className="start-button"
                      onClick={(e) => {
                        createRipple(e);
                        handleStartQuiz(quiz.quizId, quiz.lessonId, quiz.courseId, quiz.source);
                      }}
                    >
                      {quiz.attempted ? 'Retake Quiz' : 'Start Quiz'}
                      <FaArrowRight />
                    </button>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="empty-state">
                <FaClipboardCheck size={48} />
                <p>No quizzes assigned yet. Check back later!</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default ClassAssignments;


